import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import {
  ArrowLeft,
  Trash2,
  Upload,
  Loader2,
  ImagePlus,
  CheckCircle2,
  X,
  Save,
  Smile,
  PlusCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppLayout } from '@/components/AppLayout';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { useEmojiPack } from '@/hooks/useEmojiPacks';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import NotFound from './NotFound';

interface EmojiEntry {
  id: string;
  shortcode: string;
  url: string;
  file?: File;
  preview?: string;
  uploading?: boolean;
  error?: string;
}

export default function EditPackPage() {
  useSeoMeta({
    title: 'Edit Emoji Pack — EmojiVerse',
    description: 'Edit and update your custom emoji pack on Nostr.',
    ogSiteName: 'EmojiVerse',
    ogTitle: 'Edit Emoji Pack — EmojiVerse',
    ogDescription: 'Edit and update your custom emoji pack on Nostr.',
    ogImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    twitterCard: 'summary_large_image',
    twitterImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
  });

  const { naddr: naddrParam } = useParams<{ naddr: string }>();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  let pubkey = '';
  let dTag = '';
  let naddrStr = '';

  try {
    const decoded = nip19.decode(naddrParam!);
    if (decoded.type === 'naddr' && decoded.data.kind === 30030) {
      pubkey = decoded.data.pubkey;
      dTag = decoded.data.identifier;
      naddrStr = naddrParam!;
    }
  } catch {
    // fall through
  }

  const { data: pack, isLoading } = useEmojiPack(pubkey, dTag);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [packImage, setPackImage] = useState('');
  const [emojis, setEmojis] = useState<EmojiEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (pack && !initialized) {
      setTitle(pack.title);
      setDescription(pack.description);
      setPackImage(pack.image);
      setEmojis(
        pack.emojis.map((e) => ({
          id: Math.random().toString(36).slice(2),
          shortcode: e.shortcode,
          url: e.url,
        })),
      );
      setInitialized(true);
    }
  }, [pack, initialized]);

  if (!pubkey || !dTag) return <NotFound />;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48 bg-white/10" />
          <Skeleton className="h-64 w-full rounded-2xl bg-white/10" />
        </div>
      </AppLayout>
    );
  }

  if (!pack) {
    return (
      <AppLayout>
        <div className="text-center py-20">
          <p className="text-white/50">Pack not found.</p>
        </div>
      </AppLayout>
    );
  }

  // Only the owner can edit
  if (user?.pubkey !== pubkey) {
    return (
      <AppLayout>
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔒</div>
          <p className="text-white/50 text-lg">You can only edit your own packs.</p>
        </div>
      </AppLayout>
    );
  }

  const handleCoverUpload = async (file: File) => {
    setUploadingCover(true);
    try {
      const [[, url]] = await uploadFile(file);
      setPackImage(url);
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploadingCover(false);
    }
  };

  const handleEmojiFiles = async (files: FileList | null) => {
    if (!files) return;
    const newEntries: EmojiEntry[] = Array.from(files).map((file) => ({
      id: Math.random().toString(36).slice(2),
      shortcode: file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_]/g, '_'),
      url: '',
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
    }));
    setEmojis((prev) => [...prev, ...newEntries]);
  };

  const uploadEmoji = async (id: string) => {
    const entry = emojis.find((e) => e.id === id);
    if (!entry?.file || entry.url) return;
    setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, uploading: true, error: undefined } : e));
    try {
      const [[, url]] = await uploadFile(entry.file);
      setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, url, uploading: false } : e));
    } catch {
      setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, uploading: false, error: 'Failed' } : e));
    }
  };

  const uploadAllPending = async () => {
    const pending = emojis.filter((e) => !e.url && e.file);
    for (const emoji of pending) await uploadEmoji(emoji.id);
  };

  const updateEmoji = (id: string, field: 'shortcode' | 'url', value: string) => {
    setEmojis((prev) => prev.map((e) => e.id === id ? { ...e, [field]: value } : e));
  };

  const removeEmoji = (id: string) => setEmojis((prev) => prev.filter((e) => e.id !== id));

  const addManualEmoji = () => {
    setEmojis((prev) => [...prev, { id: Math.random().toString(36).slice(2), shortcode: '', url: '' }]);
  };

  const handleSave = async () => {
    const validEmojis = emojis.filter((e) => e.shortcode && e.url);
    if (validEmojis.length === 0) {
      toast({ title: 'No emojis', description: 'Add at least one emoji.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const tags: string[][] = [['d', dTag], ['title', title.trim()]];
      if (description.trim()) tags.push(['description', description.trim()]);
      if (packImage) tags.push(['image', packImage]);
      for (const emoji of validEmojis) tags.push(['emoji', emoji.shortcode, emoji.url]);

      await publish({ kind: 30030, content: '', tags });
      toast({ title: '✅ Pack updated!', description: `"${title}" has been saved.` });
      navigate(`/${naddrStr}`);
    } catch {
      toast({ title: 'Save failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const readyCount = emojis.filter((e) => e.url && e.shortcode).length;
  const pendingCount = emojis.filter((e) => e.file && !e.url).length;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        {/* Back */}
        <div className="mb-6">
          <Link
            to={`/${naddrStr}`}
            className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to pack
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Save className="w-8 h-8 text-violet-400" />
            Edit Pack
          </h1>
          <p className="text-white/50 mt-1">Update "{pack.title}"</p>
        </div>

        <div className="space-y-6">
          {/* Details */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-white/70">Pack Name *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="bg-white/10 border-white/20 text-white focus:border-violet-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Identifier</Label>
                <Input
                  value={dTag}
                  disabled
                  className="bg-white/5 border-white/10 text-white/40 font-mono text-sm cursor-not-allowed"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-white/10 border-white/20 text-white focus:border-violet-400 resize-none"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Cover Image</Label>
              <div className="flex items-center gap-4">
                {packImage ? (
                  <div className="relative group">
                    <img src={packImage} alt="cover" className="w-20 h-20 rounded-xl object-cover border border-white/20" />
                    <button onClick={() => setPackImage('')} className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ) : (
                  <div onClick={() => coverInputRef.current?.click()} className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-violet-400 transition-all">
                    {uploadingCover ? <Loader2 className="w-5 h-5 text-white/40 animate-spin" /> : <ImagePlus className="w-5 h-5 text-white/40" />}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex gap-2 mt-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} className="border-white/20 text-white/60 hover:bg-white/10 hover:text-white rounded-lg text-xs gap-1.5">
                      <Upload className="w-3 h-3" />Upload
                    </Button>
                    <Input value={packImage} onChange={(e) => setPackImage(e.target.value)} placeholder="https://..." className="bg-white/10 border-white/20 text-white placeholder:text-white/30 text-xs h-8" />
                  </div>
                </div>
              </div>
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleCoverUpload(e.target.files[0])} />
            </div>
          </div>

          {/* Emojis */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Smile className="w-4 h-4 text-violet-400" />
                Emojis
                <span className="text-white/30 text-sm font-normal">
                  ({readyCount} ready{pendingCount > 0 ? `, ${pendingCount} pending` : ''})
                </span>
              </h2>
              <div className="flex gap-2">
                {pendingCount > 0 && (
                  <Button type="button" size="sm" onClick={uploadAllPending} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded-full text-xs gap-1.5">
                    <Upload className="w-3 h-3" />Upload All
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" onClick={addManualEmoji} className="border-white/20 text-white/60 hover:bg-white/10 hover:text-white rounded-full text-xs gap-1.5">
                  <PlusCircle className="w-3 h-3" />Add URL
                </Button>
                <Button type="button" size="sm" onClick={() => fileInputRef.current?.click()} className="bg-violet-500 hover:bg-violet-400 text-white rounded-full text-xs gap-1.5">
                  <Upload className="w-3 h-3" />Upload Files
                </Button>
              </div>
            </div>

            <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
              {emojis.map((emoji) => (
                <div key={emoji.id} className={cn('flex items-center gap-3 rounded-xl p-3 border transition-all', emoji.url ? 'bg-green-500/5 border-green-500/20' : emoji.error ? 'bg-red-500/5 border-red-500/20' : 'bg-white/5 border-white/10')}>
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {(emoji.preview || emoji.url) && <img src={emoji.preview || emoji.url} alt={emoji.shortcode} className="w-full h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Input value={emoji.shortcode} onChange={(e) => updateEmoji(emoji.id, 'shortcode', e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))} placeholder="shortcode" className="bg-white/10 border-white/10 text-white placeholder:text-white/20 font-mono text-sm h-8 mb-1" />
                    <Input value={emoji.url} onChange={(e) => updateEmoji(emoji.id, 'url', e.target.value)} placeholder="https://..." className="bg-white/5 border-white/10 text-white/60 placeholder:text-white/20 text-xs h-7" />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {emoji.file && !emoji.url && (
                      <Button type="button" size="sm" onClick={() => uploadEmoji(emoji.id)} disabled={emoji.uploading} className="h-8 px-3 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-xs">
                        {emoji.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                    {emoji.url && <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />}
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEmoji(emoji.id)} className="h-8 w-8 p-0 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full py-3 rounded-xl border border-dashed border-white/15 text-white/30 text-sm hover:border-violet-400 hover:text-violet-400 transition-all">
              + Add more files
            </button>
          </div>

          {/* Save */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={submitting || !title || readyCount === 0} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white rounded-full px-8 shadow-lg shadow-violet-500/30 gap-2 disabled:opacity-50">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save Changes</>}
            </Button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleEmojiFiles(e.target.files)} />
      </div>
    </AppLayout>
  );
}
